<template>
  <v-navigation-drawer
    :model-value="Boolean(intake)"
    location="right"
    temporary
    width="480"
    @update:model-value="(value) => (value ? null : $emit('close'))"
  >
    <div v-if="intake" class="admin-intake-detail">
      <div class="admin-intake-detail__header">
        <div class="min-w-0">
          <h3>{{ $t("admin.workflows.selfRegistrationIntake.detail.title") }}</h3>
          <p>{{ intake.registration.company_name }}</p>
        </div>
        <v-btn icon variant="text" :aria-label="$t('app.actions.close')" @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-alert v-if="errorMessage" type="error" class="mb-3">
        {{ errorMessage }}
      </v-alert>
      <v-alert v-if="successMessage" type="success" class="mb-3">
        {{ successMessage }}
      </v-alert>

      <dl class="admin-intake-detail__facts">
        <template v-for="fact in facts" :key="fact.key">
          <dt>{{ fact.label }}</dt>
          <dd>{{ fact.value }}</dd>
        </template>
      </dl>

      <v-btn
        v-if="canRetry"
        color="primary"
        variant="tonal"
        block
        class="mt-4"
        :loading="retrying"
        @click="$emit('retry', intake.id)"
      >
        <v-icon start>mdi-refresh</v-icon>
        {{ $t("admin.workflows.selfRegistrationIntake.actions.retry") }}
      </v-btn>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { RETRYABLE_DISPATCH_STATUSES } from "./adminSelfRegistrationIntakeLogic.mjs";

// One placeholder for every empty fact, so the drawer does not mix "-" from the
// shared date formatter with its own em dash.
const EMPTY = "—";

export default {
  name: "AdminSelfRegistrationIntakeDetail",
  props: {
    intake: {
      type: Object,
      default: null,
    },
    retrying: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: "",
    },
    successMessage: {
      type: String,
      default: "",
    },
    // Injected so labels stay in one place: the board and the drawer render
    // the same allowlisted registration fields.
    formatDate: {
      type: Function,
      required: true,
    },
    statusLabel: {
      type: Function,
      required: true,
    },
    productLabel: {
      type: Function,
      required: true,
    },
  },
  emits: ["close", "retry"],
  computed: {
    canRetry() {
      return RETRYABLE_DISPATCH_STATUSES.has(this.intake?.dispatch?.status);
    },
    facts() {
      const intake = this.intake;
      if (!intake) return [];
      const t = (key) => this.$t(`admin.workflows.selfRegistrationIntake.detail.${key}`);
      const yesNo = (value) => (value ? this.$t("app.labels.yes") : this.$t("app.labels.no"));
      const registration = intake.registration || {};
      const dispatch = intake.dispatch || {};
      const target = intake.target || {};
      const tenant = intake.tenant || {};
      const date = (value) => this.orDash(this.formatDate(value));
      const versioned = (version, at) => (version ? `${version} · ${date(at)}` : EMPTY);

      return [
        { key: "contact", label: t("contact"), value: `${registration.first_name || ""} ${registration.last_name || ""}`.trim() || EMPTY },
        { key: "email", label: t("email"), value: registration.email || EMPTY },
        { key: "phone", label: t("phone"), value: registration.phone || EMPTY },
        { key: "company", label: t("company"), value: registration.company_name || EMPTY },
        { key: "companyIdno", label: t("companyIdno"), value: registration.company_idno || EMPTY },
        { key: "product", label: t("product"), value: this.productLabel(registration.product_key) || EMPTY },
        { key: "demo", label: t("demo"), value: yesNo(registration.demo_opt_in) },
        { key: "authMethod", label: t("authMethod"), value: this.$t(`admin.workflows.selfRegistrationIntake.authMethods.${registration.auth_method || "password"}`) },
        { key: "locale", label: t("locale"), value: String(registration.locale || "ro").toUpperCase() },
        { key: "terms", label: t("terms"), value: versioned(registration.terms_version, registration.terms_accepted_at) },
        { key: "privacy", label: t("privacy"), value: versioned(registration.privacy_version, registration.privacy_acknowledged_at) },
        { key: "dpa", label: t("dpa"), value: versioned(registration.dpa_version, registration.dpa_accepted_at) },
        { key: "marketing", label: t("marketing"), value: yesNo(registration.marketing_consent) },
        { key: "utm", label: t("utm"), value: this.formatUtm(registration.utm) },
        { key: "tenant", label: t("tenant"), value: tenant.companyName || EMPTY },
        // Raw identifiers get an explicit label instead of a bare `#`, so the
        // row stays readable without the reader guessing what the number is.
        { key: "companyId", label: t("companyId"), value: this.identifier(tenant.companyId) },
        { key: "employeeId", label: t("employeeId"), value: this.identifier(tenant.employeeId) },
        { key: "userId", label: t("userId"), value: this.identifier(tenant.userId) },
        { key: "status", label: t("status"), value: this.statusLabel(dispatch.status) },
        { key: "attempts", label: t("attempts"), value: String(dispatch.attemptCount || 0) },
        { key: "nextAttempt", label: t("nextAttempt"), value: date(dispatch.nextAttemptAt) },
        { key: "dispatchedAt", label: t("dispatchedAt"), value: date(dispatch.dispatchedAt) },
        { key: "delivery", label: t("delivery"), value: this.identifier(dispatch.externalDeliveryId) },
        { key: "error", label: t("error"), value: dispatch.lastErrorCode ? this.errorLabel(dispatch.lastErrorCode) : EMPTY },
        { key: "target", label: t("target"), value: target.companyName || EMPTY },
        { key: "endpoint", label: t("endpoint"), value: target.externalApiEndpointName || EMPTY },
        { key: "workflow", label: t("workflow"), value: target.enrichmentWorkflowInstanceId || EMPTY },
      ];
    },
  },
  methods: {
    orDash(value) {
      const normalized = String(value == null ? "" : value).trim();
      return !normalized || normalized === "-" ? EMPTY : normalized;
    },
    // The label already names the entity, so the value stays a plain number.
    identifier(value) {
      const parsed = Number(value);
      return Number.isInteger(parsed) && parsed > 0 ? String(parsed) : EMPTY;
    },
    formatUtm(utm) {
      if (!utm || typeof utm !== "object") return EMPTY;
      const entries = Object.entries(utm).filter(([, value]) => Boolean(value));
      if (!entries.length) return EMPTY;
      return entries.map(([key, value]) => `${key}=${value}`).join(", ");
    },
    errorLabel(code) {
      const key = `admin.workflows.selfRegistrationIntake.errorCodes.${code}`;
      const translated = this.$t(key);
      return translated === key ? code : translated;
    },
  },
};
</script>

<style scoped>
.admin-intake-detail {
  /* The app shell centers text globally; a definition list has to read
     left-to-right to stay scannable. */
  padding: 20px;
  text-align: start;
}

.admin-intake-detail__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.admin-intake-detail__header h3 {
  margin: 0;
  color: var(--app-text-strong);
  font-size: 1.05rem;
  font-weight: 700;
}

.admin-intake-detail__header p {
  margin: 4px 0 0;
  color: var(--app-text-default);
  opacity: 0.68;
}

.admin-intake-detail__facts {
  display: grid;
  grid-template-columns: minmax(0, 40%) minmax(0, 60%);
  gap: 8px 12px;
  margin: 0;
}

.admin-intake-detail__facts dt {
  color: var(--app-text-default);
  opacity: 0.68;
  font-size: 0.82rem;
}

.admin-intake-detail__facts dd {
  margin: 0;
  color: var(--app-text-strong);
  font-size: 0.88rem;
  overflow-wrap: anywhere;
}
</style>
